import React, { useCallback, useRef } from 'react';
import { AbsoluteFill, OffthreadVideo, useVideoConfig } from 'remotion';
import { resolveMediaSrc } from '../../shared/media';
import { coverRect, type HeroSegment } from '../timeline';
import { DemandThread } from './DemandThread';

/** Matte edge feather in composition pixels (spec: 2–4 px). */
export const MATTE_FEATHER_PX = 3;

const COVER_STYLE: React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };

function sourceSize(src: CanvasImageSource): { w: number; h: number } {
  if (typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement) {
    return { w: src.videoWidth, h: src.videoHeight };
  }
  if (typeof HTMLImageElement !== 'undefined' && src instanceof HTMLImageElement) {
    return { w: src.naturalWidth, h: src.naturalHeight };
  }
  const s = src as { width?: unknown; height?: unknown };
  return {
    w: typeof s.width === 'number' ? s.width : 0,
    h: typeof s.height === 'number' ? s.height : 0,
  };
}

/**
 * One content segment: the clip cover-fitted to the frame, the demand thread above it and — when the clip ships an
 * occlusion matte — a re-plate of the clip masked by that matte on top, so the thread passes behind the subject.
 *
 * The re-plate is a canvas fed by Remotion's `onVideoFrame` (the extracted frame image during renders, the video
 * element in the Studio). Frame drawing is a pure function of the two current frames, so renders stay deterministic.
 */
export const ClipLayer: React.FC<{ segment: HeroSegment; clipVolume: number }> = ({ segment, clipVolume }) => {
  const { width, height } = useVideoConfig();
  const clipRef = useRef<CanvasImageSource | null>(null);
  const matteRef = useRef<CanvasImageSource | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scratchRef = useRef<HTMLCanvasElement | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const clip = clipRef.current;
    const matte = matteRef.current;
    if (!canvas || !clip || !matte) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const cs = sourceSize(clip);
    const ms = sourceSize(matte);
    if (!cs.w || !cs.h || !ms.w || !ms.h) return;
    const W = canvas.width;
    const H = canvas.height;

    // 1. Matte luminance → alpha on a scratch canvas (grayscale: red channel is the luminance).
    const scratch = scratchRef.current ?? (scratchRef.current = document.createElement('canvas'));
    if (scratch.width !== W || scratch.height !== H) {
      scratch.width = W;
      scratch.height = H;
    }
    const sctx = scratch.getContext('2d', { willReadFrequently: true });
    if (!sctx) return;
    const mr = coverRect(ms.w, ms.h, W, H);
    sctx.globalCompositeOperation = 'source-over';
    sctx.filter = 'none';
    sctx.clearRect(0, 0, W, H);
    sctx.drawImage(matte, mr.x, mr.y, mr.w, mr.h);
    const img = sctx.getImageData(0, 0, W, H);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i + 3] = d[i]!;
    }
    sctx.putImageData(img, 0, 0);

    // 2. Re-plate: the clip, kept only where the feathered matte is white.
    const cr = coverRect(cs.w, cs.h, W, H);
    ctx.globalCompositeOperation = 'source-over';
    ctx.filter = 'none';
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(clip, cr.x, cr.y, cr.w, cr.h);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.filter = `blur(${MATTE_FEATHER_PX}px)`;
    ctx.drawImage(scratch, 0, 0);
    ctx.filter = 'none';
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  const onClipFrame = useCallback(
    (frame: CanvasImageSource) => {
      clipRef.current = frame;
      draw();
    },
    [draw],
  );
  const onMatteFrame = useCallback(
    (frame: CanvasImageSource) => {
      matteRef.current = frame;
      draw();
    },
    [draw],
  );

  const hasMatte = Boolean(segment.matteUrl);

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      <OffthreadVideo
        src={resolveMediaSrc(segment.url)}
        trimBefore={segment.trimBefore || undefined}
        volume={clipVolume}
        muted={clipVolume === 0}
        style={COVER_STYLE}
        onVideoFrame={hasMatte ? onClipFrame : undefined}
      />
      {segment.thread ? <DemandThread thread={segment.thread} durationInFrames={segment.durationInFrames} /> : null}
      {hasMatte ? (
        <>
          <OffthreadVideo
            src={resolveMediaSrc(segment.matteUrl!)}
            trimBefore={segment.trimBefore || undefined}
            muted
            style={{ position: 'absolute', left: 0, top: 0, width: 2, height: 2, opacity: 0, pointerEvents: 'none' }}
            onVideoFrame={onMatteFrame}
          />
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}
          />
        </>
      ) : null}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
